import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, Transition as _Transition } from "vue"
import _imports_0 from '@/assets/pencil-gray.svg'


const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "w-full h-screen fixed top-0 left-0 flex items-start justify-center bg-black/75 z-[1000]" }
const _hoisted_3 = { class: "flex gap-2 items-center" }
const _hoisted_4 = { class: "text-xl font-semibold" }

import client from "@/utils/axios";
import { errorToast, successToast } from "@/utils/custom-toast";
import { ref, computed } from "vue";
import { Question } from "@/types";
import QuestionnaireModal from "@/components/Questionnaire/parts/QuestionnaireModal.vue";
import QuestionnaireProgressBar from "@/components/Questionnaire/parts/QuestionnaireProgressBar.vue";
import AbnahmeApointmentCompleted from "@/components/Questionnaire/abnahme/AbnahmeApointmentCompleted.vue";
import AbnahmeAppointmentNotCompleted from "@/components/Questionnaire/abnahme/AbnahmeAppointmentNotCompleted.vue";
import AbnahmeAppointmentFinal from "@/components/Questionnaire/abnahme/AbnahmeAppointmentFinal.vue";
import AbnahmeDataMissingTextArea from "../Questionnaire/abnahme/AbnahmeDataMissingTextArea.vue";
import { parseQcAbnahmeAnswers } from "@/utils/parse-qc-abnahme-answers";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isQcModalOpen: boolean;
  projectId: string;
  phaseId: number;
  phaseDetails: any;
};

type Emits = {
  (event: "onClose"): void;
  (event: "onSubmit"): void;
  (event: "generateReport"): void;
};

export default /*@__PURE__*/_defineComponent({
  __name: 'QCModal',
  props: {
    isQcModalOpen: { type: Boolean },
    projectId: {},
    phaseId: {},
    phaseDetails: {}
  },
  emits: ["onClose", "onSubmit", "generateReport"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;
const { t } = useTranslate();
const questions = [
  {
    slug: "qc-initial",
    modalTitle: t("questionnaire.completing_qc").value,
    question: t("questionnaire.what_is_outcome").value,
    answers: ["qc-passed", "data-missing", "qc-failed"],
    value: "",
  },
  {
    slug: "qc-passed",
    modalTitle: t("questionnaire.review_qc").value,
    answers: [],
    data: {
      ibnDate: "",
    },
    value: "",
  },
  {
    slug: "data-missing",
    modalTitle: t("questionnaire.completing_qc").value,
    answers: [],
    data: {
      description: "",
    },
    value: "",
  },
  {
    slug: "qc-failed",
    modalTitle: t("questionnaire.completing_qc").value,
    answers: [],
    data: {
      description: "",
      whatToSchedule: "",
      days: 0,
      missingMaterial: "",
      dispoNotes: "",
      fieldInstruction: "",
    },
    value: "",
  },
  {
    slug: "data-missing-review",
    modalTitle: t("questionnaire.review_qc").value,
    answers: [],
    data: null,
    value: "",
  },
  {
    slug: "qc-failed-review",
    modalTitle: t("questionnaire.review_qc").value,
    answers: [],
    data: null,
    value: "",
  },
];

const questionsState = ref<Array<Question>>([...questions]);
const isModalOpen = computed(() => props.isQcModalOpen);
const isLoading = ref(false);
const selectedQuestion = ref<Question | undefined>(
  questionsState.value
    ? questionsState.value.find((item: Question) => item.slug === "qc-initial")
    : undefined
);

const currentPhase = computed(() => {
  let currentPhaseProgress = props.phaseDetails?.phases.find(
    (phase: any) => phase.phaseId === props.phaseId
  );
  return currentPhaseProgress;
});

const questionnaireProgress = computed(() => {
  let percentage = 100;
  if (selectedQuestion.value && selectedQuestion.value) {
    switch (selectedQuestion.value.slug) {
      case "qc-initial":
        percentage = 15;
        break;
      case "appointment-not-completed":
      case "data-missing":
      case "qc-failed":
        percentage = 70;
        break;
      case "qc-passed":
      case "data-missing-review":
      case "qc-failed-review":
        percentage = 100;
        break;
    }
  }

  return percentage;
});

// QC PASSED PATH
const onQcPassed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "qc-passed"
  );
};

const onQcPassedSubmit = (ibnDate: string, generateReport?: boolean) => {
  let payload = {
    actionId: 8,
    ibnDate: ibnDate,
  };

  onSubmit(payload, generateReport);
};

const dataMissingReviewQuestions = computed(() => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    return [
      {
        question: t("questionnaire.what_data_is_missing").value,
        answer: dataMissingQuestion.data.description,
      },
    ];
  }
  return [];
});

const qcFailedReviewQuestions = computed(() => {
  let qcFailedData = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
  if (qcFailedData && qcFailedData.data) {
    let tempQuestions = parseQcAbnahmeAnswers(qcFailedData.data, "qc");
    return tempQuestions;
  }
  return [];
});

const onDataMissing = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
};
const dataMissingOnSubmit = (description: string) => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing-review"
  );
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    dataMissingQuestion.data.description = description;
  }
};

const dataMissingReviewOnSubmit = () => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    let payload = {
      actionId: 6,
      description: dataMissingQuestion.data.description,
    };

    onSubmit(payload, false);
  }
};

const onQcFailed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
};

const onQcFailedSubmit = (payload: {
  description: string;
  whatToSchedule: string;
  days: string;
  missingMaterial: string;
  dispoNotes: string;
  fieldInstruction: string;
}) => {
  let qcFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
  if (qcFailedQuestion && qcFailedQuestion.data) {
    qcFailedQuestion.data = payload;
  }
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed-review"
  );
};

const onQcFailedReviewSubmit = () => {
  let qcFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
  if (qcFailedQuestion && qcFailedQuestion.data) {
    let payload = {
      actionId: 7,
      metadata: qcFailedQuestion.data,
    };
    onSubmit(payload);
  }
};

const onClose = () => {
  if (questionsState.value) {
    questionsState.value = [...questions];
  }
  selectedQuestion.value = questionsState.value
    ? questionsState.value.find((item: Question) => item.slug === "qc-initial")
    : undefined;
  emits("onClose");
};

const onStepBack = (goBackToQuestionSlug: string) => {
  let backQuestion = questionsState.value.find(
    (item: Question) => item.slug === goBackToQuestionSlug
  );

  if (backQuestion) {
    selectedQuestion.value = backQuestion;
  }
};

const onSubmit = async (payload: any, generateReport?: boolean) => {
  try {
    isLoading.value = true;
    payload.isQc = true;
    let resp = await client.post(
      `/projects/${props.projectId}/phase/${props.phaseId}/complete-abnahme`,
      payload
    );

    if (resp.status === 200) {
      successToast("QC action saved.");
      if (generateReport) {
        emits("generateReport");
      }
    }
    isLoading.value = false;
    onClose();
    emits("onClose");
  } catch (error: any) {
    console.error(error);
    errorToast(error);
    isLoading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "nested" }, {
    default: _withCtx(() => [
      (isModalOpen.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(QuestionnaireModal, {
                class: "modal",
                onOnClose: onClose
              }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _cache[6] || (_cache[6] = _createElementVNode("img", {
                      class: "w-4 h-4 cursor-pointer",
                      src: _imports_0,
                      alt: "Pencil"
                    }, null, -1)),
                    _createElementVNode("h2", _hoisted_4, _toDisplayString(selectedQuestion.value ? selectedQuestion.value.modalTitle : ""), 1)
                  ])
                ]),
                "progress-bar": _withCtx(() => [
                  _createVNode(QuestionnaireProgressBar, {
                    progress: questionnaireProgress.value,
                    warning: false
                  }, null, 8, ["progress"])
                ]),
                content: _withCtx(() => [
                  (selectedQuestion.value && selectedQuestion.value?.slug === 'qc-initial')
                    ? (_openBlock(), _createBlock(AbnahmeApointmentCompleted, {
                        key: 0,
                        "appointment-completed-button-text": 
                _unref(t)('questionnaire.qc_passed').value
              ,
                        "data-is-missing-button-text": 
                _unref(t)('questionnaire.data_is_missing').value
              ,
                        "apointment-failed-button-text": 
                _unref(t)('questionnaire.qc_failed').value
              ,
                        "prev-button-text": _unref(t)('questionnaire.step_back').value,
                        onOnAbnahmePassed: onQcPassed,
                        onOnDataMissing: onDataMissing,
                        onOnAbnahmeFailed: onQcFailed,
                        onOnStepBack: _cache[0] || (_cache[0] = ($event: any) => (onStepBack('qc-initial')))
                      }, null, 8, ["appointment-completed-button-text", "data-is-missing-button-text", "apointment-failed-button-text", "prev-button-text"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value && selectedQuestion.value?.slug === 'data-missing'
              )
                    ? (_openBlock(), _createBlock(AbnahmeDataMissingTextArea, {
                        key: 1,
                        textAreaTitle: _unref(t)('questionnaire.what_data_is_missing').value,
                        textAreaPlaceholder: _unref(t)('questionnaire.description').value,
                        prevButtonText: _unref(t)('questionnaire.step_back').value,
                        nextButtonText: _unref(t)('questionnaire.next').value,
                        height: "h-[203px]",
                        description: questionsState.value.find( (item) =>
                  item.slug === 'data-missing')?.data.description,
                        onOnSubmit: dataMissingOnSubmit,
                        onOnStepBack: _cache[1] || (_cache[1] = ($event: any) => (onStepBack('qc-initial')))
                      }, null, 8, ["textAreaTitle", "textAreaPlaceholder", "prevButtonText", "nextButtonText", "description"]))
                    : _createCommentVNode("", true),
                  (selectedQuestion.value && selectedQuestion.value?.slug === 'qc-failed')
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentNotCompleted, {
                        key: 2,
                        title: _unref(t)('questionnaire.qc_failed').value,
                        "next-button-text": _unref(t)('questionnaire.next').value,
                        "prev-button-text": _unref(t)('questionnaire.step_back').value,
                        "text-area-placeholder-one": _unref(t)('questionnaire.description').value,
                        "text-area-title-one": 
                _unref(t)('questionnaire.write_what_happened').value
              ,
                        "select-menu-data-one": [
                { id: 'ac', name: 'AC' },
                { id: 'dc', name: 'DC' },
                { id: 'both', name: 'Both' },
              ],
                        "select-menu-data-one-title": 
                _unref(t)('questionnaire.what_do_we_schedule').value
              ,
                        "text-field-one-text": _unref(t)('questionnaire.how_many_days').value,
                        "text-field-one-placeholder": _unref(t)('questionnaire.number').value,
                        "text-area-title-two": 
                _unref(t)('questionnaire.what_material_is_missing').value
              ,
                        "text-area-placeholder-two": _unref(t)('questionnaire.description').value,
                        "text-area-title-three": _unref(t)('questionnaire.dispo_notes').value,
                        "text-area-placeholder-three": 
                _unref(t)('questionnaire.description').value
              ,
                        "text-area-title-four": 
                _unref(t)('questionnaire.what_needs_to_be_done').value
              ,
                        "text-area-placeholder-four": _unref(t)('questionnaire.description').value,
                        "text-field-one-required": true,
                        data: questionsState.value.find(
                (item) => item.slug === 'qc-failed' )?.data,
                        onOnStepBack: _cache[2] || (_cache[2] = ($event: any) => (onStepBack('qc-initial'))),
                        onOnSubmit: onQcFailedSubmit
                      }, null, 8, ["title", "next-button-text", "prev-button-text", "text-area-placeholder-one", "text-area-title-one", "select-menu-data-one-title", "text-field-one-text", "text-field-one-placeholder", "text-area-title-two", "text-area-placeholder-two", "text-area-title-three", "text-area-placeholder-three", "text-area-title-four", "text-area-placeholder-four", "data"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value &&
                selectedQuestion.value?.slug === 'data-missing-review'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 3,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        "is-loading": isLoading.value,
                        status: "data-missing",
                        "done-button-text": _unref(t)('questionnaire.submit').value,
                        "final-title": _unref(t)('questionnaire.data_missing').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        answers: dataMissingReviewQuestions.value,
                        onOnStepBack: _cache[3] || (_cache[3] = ($event: any) => (onStepBack('data-missing'))),
                        onOnSubmit: dataMissingReviewOnSubmit
                      }, null, 8, ["current-phase", "phase-details", "is-loading", "done-button-text", "final-title", "prev-button-text", "answers"]))
                    : _createCommentVNode("", true),
                  (selectedQuestion.value && selectedQuestion.value?.slug === 'qc-passed')
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 4,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        "is-loading": isLoading.value,
                        "text-field-title": `IBN date ${
                currentPhase.value?.phaseId === 2
                  ? '(' + _unref(t)('global.required').value + ')'
                  : '(' + _unref(t)('global.optional').value + ')'
              }`,
                        status: "success",
                        "done-button-text": _unref(t)('questionnaire.done').value,
                        "final-title": _unref(t)('questionnaire.fantastic').value,
                        "final-subtitle": _unref(t)('questionnaire.qc_passed').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        onOnStepBack: _cache[4] || (_cache[4] = ($event: any) => (onStepBack('qc-initial'))),
                        onOnSubmit: onQcPassedSubmit
                      }, null, 8, ["current-phase", "phase-details", "is-loading", "text-field-title", "done-button-text", "final-title", "final-subtitle", "prev-button-text"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value &&
                selectedQuestion.value?.slug === 'qc-failed-review'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 5,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        "is-loading": isLoading.value,
                        status: "failed",
                        "done-button-text": _unref(t)('questionnaire.submit').value,
                        "final-title": _unref(t)('questionnaire.qc_failed').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        answers: qcFailedReviewQuestions.value,
                        onOnStepBack: _cache[5] || (_cache[5] = ($event: any) => (onStepBack('qc-failed'))),
                        onOnSubmit: onQcFailedReviewSubmit
                      }, null, 8, ["current-phase", "phase-details", "is-loading", "done-button-text", "final-title", "prev-button-text", "answers"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})